import { useField, useFormikContext } from 'formik'
import { Divider, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { IVehicleType, ResultObjectType } from '@cbgms/api'
import { Loader, SearchInput } from '@carsys/ui'
import { NoVehicleSearchResults, SearchResults } from 'app/search/Dialog/SearchResults'
import { useSearch } from 'app/search/hooks/useSearch'
import { ProviderContainer } from './components/ProviderContainer'
import SelectedValue from './components/fields/SelectedValue'
import { StepOption, useSelectionContext } from './context/SelectionContext'
import { VehicleMakeImage } from '../DashboardDetails/VehicleMakeImage.Mui'
import { useIsFetching } from '@cbgms/base/http/use-query'
import { useGetBrandsCacheKey, useGetModelsCacheKey, useGetTypesCacheKey } from './components/providers/hooks/cacheKeys'
import { UnknownVehicleResultItem } from './components/UnknownVehicleResultItem'
import { FieldName_Provider } from 'app/vehicles/components/UnknownVehicleV2/components/providers/const'
import { UnknownVehicleFormValues } from './UnknownVehicleV2Form'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { SelectedVehicleSummary } from 'app/vehicles/components/UnknownVehicleV2/SelectedVehicleSummary'
import { Confirm } from './components/steps/Confirm/Confirm'
import { VehicleLinkingModals } from './components/VehicleLinkingModals'

const searchTypes = [ResultObjectType.DatabaseVehicle, ResultObjectType.GenericVehicle]

/*
  Components for vehicle selection without dialog.
  Can be used in combination with form.
*/
export const UnknownVehicleV2: React.FC<{ slotBottomButtons?: React.ReactNode; typeSelectMode?: boolean }> = ({
  slotBottomButtons,
  typeSelectMode
}) => {
  const { t } = useTranslation()
  const search = useSearch(searchTypes)
  const showSearchResults = !!search.query

  const [{ value: provider }] = useField(FieldName_Provider)
  const [{ value: country }] = useField('Country')
  const { brand, model, type, currentStep, setStep } = useSelectionContext()

  const isFetchingBrands = useIsFetching(useGetBrandsCacheKey(provider, country))
  const isFetchingModels = useIsFetching(useGetModelsCacheKey(provider, country, brand?.key))
  const isFetchingTypes = useIsFetching(useGetTypesCacheKey(provider, country, brand?.key, model?.key))
  const isDone = currentStep === StepOption.DONE
  const isConfirm = currentStep === StepOption.CONFIRM
  const { submitForm, isSubmitting, values, setFieldValue } = useFormikContext<UnknownVehicleFormValues>()
  const forceSubmit = typeSelectMode && currentStep === StepOption.CONFIRM
  useEffect(() => {
    if (forceSubmit) {
      submitForm()
    }
  }, [forceSubmit])
  // we want to be able to navigate straight to the confirm view when we've navigated here from a different flow
  // using only VIN, so we can set licenseplate for the vehicle before creating it.
  const { state } = useLocation<IVehicleType>()
  useEffect(() => {
    if (state) {
      setStep(StepOption.CONFIRM)
      setFieldValue('vehicle', state)
      setFieldValue('Vin', state.GenericVehicle?.VIN)
    }
  }, [state])

  if (forceSubmit && isSubmitting) {
    return <Loader loading />
  }

  if (forceSubmit && !isSubmitting && values.vehicle) {
    // this was a type-selection. show the information of the selected type.
    // some usages will navigate away imediatly on submit, others will ask user to confirm/
    // we will show this summary while waiting for the user to confirm
    return <SelectedVehicleSummary vehicle={values.vehicle} />
  }

  return (
    <Stack spacing={2} sx={{ width: '100%' }} data-testid='unknown-vehicle-v2-provider'>
      {!isDone && (
        <Stack spacing={1} direction='row'>
          {!typeSelectMode && (
            <>
              <SearchInput
                value={search.query}
                autoComplete='off'
                onChange={e => search.setQuery(e.target.value)}
                placeholder={t('vehicleLookup_widget_searchPlaceholder')}
                variant='outlined'
                size='small'
                loading={search.loading}
                inputProps={{ autoFocus: true, name: 'search-input' }}
                fullWidth
              />
              <Divider orientation='vertical' sx={{ height: 'auto' }}>
                {t('unknown_vehicle.or_by')}
              </Divider>
            </>
          )}
          <SelectedValue
            isLoading={!!isFetchingBrands}
            label={t(`vehicle.brand`)}
            value={brand && brand.name}
            step={StepOption.BRAND}
            currentStep={currentStep}
            disabled={false}
            icon={brand && <VehicleMakeImage manufacturer={brand.key} imageSize='tiny' />}
            onStepChange={() => {
              setStep(StepOption.BRAND)
            }}
          />
          <SelectedValue
            isLoading={!!isFetchingModels}
            label={t(`vehicle.model`)}
            value={model && model.name}
            step={StepOption.MODEL}
            currentStep={currentStep}
            disabled={!brand}
            onStepChange={() => {
              setStep(StepOption.MODEL)
            }}
          />
          <SelectedValue
            isLoading={!!isFetchingTypes}
            label={t(`vehicle.type`)}
            value={type && type.name}
            step={StepOption.TYPE}
            currentStep={currentStep}
            disabled={!model}
            onStepChange={() => {
              setStep(StepOption.TYPE)
            }}
          />
        </Stack>
      )}
      {showSearchResults ? (
        isConfirm ? (
          <Confirm slotBottomButtons={slotBottomButtons} />
        ) : (
          <>
            <Divider flexItem />
            <SearchResults
              {...search}
              ResultItem={UnknownVehicleResultItem}
              NoSearchResult={NoVehicleSearchResults}
              noResultCallBack={search.clearQuery}
            />
          </>
        )
      ) : (
        <ProviderContainer slotBottomButtons={slotBottomButtons} />
      )}

      {/* Render modals for vehicle linking when in portal mode */}
      <VehicleLinkingModals />
    </Stack>
  )
}
